import { useCallback, useEffect, useState } from "react";
import { Factory, Loader2, Plus, Trash2 } from "lucide-react";
import { api, apiError, inr, shortDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Empty, ListState, PageHeader } from "@/components/Layout";
import { Field } from "@/pages/Leads";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const uid = () => `row-${Math.random().toString(36).slice(2, 10)}`;
const BLANK_BOM_LINE = { uid: "", stock_item_id: "", name: "", quantity: 1, unit: "kg", rate: 0, wastage_percent: 0 };

function statusVariant(status) {
  if (status === "Completed") return "default";
  if (status === "Planned") return "outline";
  return "secondary";
}

export default function Manufacturing() {
  const { can } = useAuth();
  const [boms, setBoms] = useState([]);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [stock, setStock] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bomOpen, setBomOpen] = useState(false);
  const [orderOpen, setOrderOpen] = useState(false);
  const [issueOrder, setIssueOrder] = useState(null);
  const [issueQty, setIssueQty] = useState({});
  const [finishOrder, setFinishOrder] = useState(null);
  const [finish, setFinish] = useState({ produced_quantity: "", warehouse_id: "", notes: "" });
  const [bom, setBom] = useState({
    product_id: "", output_quantity: 1, labour_cost: 0, overhead_cost: 0, notes: "",
    items: [{ ...BLANK_BOM_LINE, uid: uid() }],
  });
  const [order, setOrder] = useState({ bom_id: "", quantity: 1, target_date: "", warehouse_id: "", notes: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, o, p, s, w] = await Promise.all([
        api.get("/boms"),
        api.get("/production-orders"),
        api.get("/products?limit=200"),
        api.get("/stock"),
        api.get("/warehouses"),
      ]);
      setBoms(b.data);
      setOrders(o.data);
      setProducts(p.data.items);
      setStock(s.data.items);
      setWarehouses(w.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const bomTotal = bom.items.reduce(
    (sum, i) => sum + Number(i.quantity || 0) * (1 + Number(i.wastage_percent || 0) / 100) * Number(i.rate || 0),
    Number(bom.labour_cost || 0) + Number(bom.overhead_cost || 0)
  );

  const setLine = (i, patch) => {
    const items = [...bom.items];
    items[i] = { ...items[i], ...patch };
    setBom({ ...bom, items });
  };

  const saveBom = async () => {
    try {
      if (!bom.product_id) return toast.error("Select the finished product");
      const items = bom.items
        .filter((i) => i.stock_item_id)
        .map(({ uid: _u, ...i }) => ({
          ...i, quantity: Number(i.quantity || 0), rate: Number(i.rate || 0),
          wastage_percent: Number(i.wastage_percent || 0),
        }));
      if (!items.length) return toast.error("Add at least one raw material");
      await api.post("/boms", {
        ...bom, items,
        output_quantity: Number(bom.output_quantity || 1),
        labour_cost: Number(bom.labour_cost || 0),
        overhead_cost: Number(bom.overhead_cost || 0),
      });
      toast.success("BOM created");
      setBomOpen(false);
      setBom({ product_id: "", output_quantity: 1, labour_cost: 0, overhead_cost: 0, notes: "", items: [{ ...BLANK_BOM_LINE, uid: uid() }] });
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const saveOrder = async () => {
    try {
      if (!order.bom_id) return toast.error("Select a BOM");
      const wh = warehouses.find((w) => w.id === order.warehouse_id);
      await api.post("/production-orders", {
        ...order, quantity: Number(order.quantity || 0), warehouse_name: wh?.name || "",
      });
      toast.success("Production order created");
      setOrderOpen(false);
      setOrder({ bom_id: "", quantity: 1, target_date: "", warehouse_id: "", notes: "" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const submitIssue = async () => {
    try {
      const lines = Object.entries(issueQty)
        .filter(([, v]) => Number(v) > 0)
        .map(([index, v]) => ({ index: Number(index), quantity: Number(v) }));
      if (!lines.length) return toast.error("Enter the quantity being issued");
      await api.post(`/production-orders/${issueOrder.id}/issue`, { lines });
      toast.success("Materials issued — raw material stock reduced");
      setIssueOrder(null);
      setIssueQty({});
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const submitFinish = async () => {
    try {
      if (!Number(finish.produced_quantity)) return toast.error("Enter the produced quantity");
      const wh = warehouses.find((w) => w.id === finish.warehouse_id);
      await api.post(`/production-orders/${finishOrder.id}/complete`, {
        produced_quantity: Number(finish.produced_quantity),
        warehouse_id: finish.warehouse_id,
        warehouse_name: wh?.name || "",
        notes: finish.notes,
      });
      toast.success("Finished goods added to stock");
      setFinishOrder(null);
      setFinish({ produced_quantity: "", warehouse_id: "", notes: "" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div>
      <PageHeader
        testId="manufacturing-header"
        title="Manufacturing"
        subtitle={`${boms.length} bills of material • ${orders.length} production orders`}
        actions={
          <>
            {can("bom", "create") && (
              <Dialog open={bomOpen} onOpenChange={setBomOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" data-testid="new-bom-button" className="gap-1.5">
                    <Plus className="h-4 w-4" /> New BOM
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
                  <DialogHeader>
                    <DialogTitle>Bill of material</DialogTitle>
                  </DialogHeader>
                  <div className="grid gap-3 sm:grid-cols-3">
                    <Field label="Finished product" className="sm:col-span-2">
                      <Select value={bom.product_id} onValueChange={(v) => setBom({ ...bom, product_id: v })}>
                        <SelectTrigger data-testid="bom-product-select">
                          <SelectValue placeholder="Select product" />
                        </SelectTrigger>
                        <SelectContent>
                          {products.map((p) => (
                            <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field label="Output quantity">
                      <Input
                        data-testid="bom-output-input"
                        value={bom.output_quantity}
                        onChange={(e) => setBom({ ...bom, output_quantity: e.target.value })}
                      />
                    </Field>
                  </div>
                  <div className="overline mt-2">Raw materials</div>
                  {bom.items.map((it, i) => (
                    <div key={it.uid} className="grid grid-cols-2 items-end gap-2 sm:grid-cols-6">
                      <Field label="Material" className="col-span-2">
                        <Select
                          value={it.stock_item_id}
                          onValueChange={(v) => {
                            const s = stock.find((x) => x.id === v);
                            setLine(i, { stock_item_id: v, name: s?.name || "", unit: s?.unit || "kg", rate: s?.rate || 0 });
                          }}
                        >
                          <SelectTrigger data-testid={`bom-material-select-${i}`}>
                            <SelectValue placeholder="Select stock item" />
                          </SelectTrigger>
                          <SelectContent>
                            {stock.map((s) => (
                              <SelectItem key={s.id} value={s.id}>
                                {s.name} ({s.quantity} {s.unit})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </Field>
                      {[["quantity", "Qty"], ["rate", "Rate ₹"], ["wastage_percent", "Wastage %"]].map(([k, l]) => (
                        <Field key={k} label={l}>
                          <Input
                            data-testid={`bom-${k}-input-${i}`}
                            value={it[k]}
                            onChange={(e) => setLine(i, { [k]: e.target.value })}
                          />
                        </Field>
                      ))}
                      <Button
                        size="icon"
                        variant="ghost"
                        data-testid={`bom-remove-line-${i}`}
                        onClick={() => setBom({ ...bom, items: bom.items.filter((x) => x.uid !== it.uid) })}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid="bom-add-line-button"
                    onClick={() => setBom({ ...bom, items: [...bom.items, { ...BLANK_BOM_LINE, uid: uid() }] })}
                  >
                    Add material
                  </Button>
                  <div className="grid gap-3 sm:grid-cols-3">
                    <Field label="Labour cost ₹">
                      <Input
                        data-testid="bom-labour-input"
                        value={bom.labour_cost}
                        onChange={(e) => setBom({ ...bom, labour_cost: e.target.value })}
                      />
                    </Field>
                    <Field label="Overhead cost ₹">
                      <Input
                        data-testid="bom-overhead-input"
                        value={bom.overhead_cost}
                        onChange={(e) => setBom({ ...bom, overhead_cost: e.target.value })}
                      />
                    </Field>
                    <Field label="Estimated total cost">
                      <Input readOnly data-testid="bom-total-cost" value={inr(bomTotal)} />
                    </Field>
                  </div>
                  <Field label="Notes">
                    <Textarea value={bom.notes} onChange={(e) => setBom({ ...bom, notes: e.target.value })} />
                  </Field>
                  <DialogFooter>
                    <Button data-testid="bom-save-button" onClick={saveBom}>Save BOM</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
            {can("production", "create") && (
              <Dialog open={orderOpen} onOpenChange={setOrderOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="new-production-order-button" className="gap-1.5">
                    <Factory className="h-4 w-4" /> Production Order
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-lg">
                  <DialogHeader>
                    <DialogTitle>New production order</DialogTitle>
                  </DialogHeader>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Field label="Bill of material" className="sm:col-span-2">
                      <Select value={order.bom_id} onValueChange={(v) => setOrder({ ...order, bom_id: v })}>
                        <SelectTrigger data-testid="order-bom-select">
                          <SelectValue placeholder="Select BOM" />
                        </SelectTrigger>
                        <SelectContent>
                          {boms.map((b) => (
                            <SelectItem key={b.id} value={b.id}>
                              {b.product_name} — {inr(b.cost_per_unit)}/unit
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field label="Quantity to produce">
                      <Input
                        data-testid="order-quantity-input"
                        value={order.quantity}
                        onChange={(e) => setOrder({ ...order, quantity: e.target.value })}
                      />
                    </Field>
                    <Field label="Target date">
                      <Input
                        type="date"
                        data-testid="order-target-date-input"
                        value={order.target_date}
                        onChange={(e) => setOrder({ ...order, target_date: e.target.value })}
                      />
                    </Field>
                    <Field label="Finished goods warehouse" className="sm:col-span-2">
                      <Select value={order.warehouse_id} onValueChange={(v) => setOrder({ ...order, warehouse_id: v })}>
                        <SelectTrigger data-testid="order-warehouse-select">
                          <SelectValue placeholder="Select warehouse" />
                        </SelectTrigger>
                        <SelectContent>
                          {warehouses.map((w) => (
                            <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                  </div>
                  <DialogFooter>
                    <Button data-testid="order-save-button" onClick={saveOrder}>Create order</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </>
        }
      />

      <Tabs defaultValue="orders" className="px-4 pt-4 sm:px-6">
        <TabsList data-testid="manufacturing-tabs">
          <TabsTrigger value="orders">Production orders</TabsTrigger>
          <TabsTrigger value="boms">Bills of material</TabsTrigger>
        </TabsList>

        <TabsContent value="orders">
          <ListState loading={loading} isEmpty={orders.length === 0}
            emptyTitle="No production orders"
            emptyHint="Create a BOM first, then raise a production order.">
              <div className="surface overflow-x-auto" data-testid="production-orders-table">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Order</TableHead>
                      <TableHead>Product</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="hidden text-right md:table-cell">Material cost</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orders.map((o) => (
                      <TableRow key={o.id} data-testid={`production-row-${o.id}`}>
                        <TableCell className="font-mono-num text-xs">
                          {o.number}
                          <div className="text-[10px] text-muted-foreground">{shortDate(o.created_at)}</div>
                        </TableCell>
                        <TableCell className="text-sm">
                          {o.product_name}
                          {(o.requirements || []).some((r) => Number(r.shortfall) > 0) && (
                            <Badge variant="destructive" className="ml-2 text-[9px]">SHORTFALL</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-mono-num text-sm">
                          {o.produced_quantity ? `${o.produced_quantity}/${o.quantity}` : o.quantity}
                        </TableCell>
                        <TableCell>
                          <Badge variant={statusVariant(o.status)} className="text-[10px]">{o.status}</Badge>
                        </TableCell>
                        <TableCell className="hidden text-right font-mono-num text-sm md:table-cell">
                          {inr(o.actual_material_cost || o.planned_material_cost)}
                        </TableCell>
                        <TableCell className="space-x-1 text-right">
                          {o.status !== "Completed" && can("production", "edit") && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                data-testid={`issue-materials-button-${o.id}`}
                                onClick={() => { setIssueOrder(o); setIssueQty({}); }}
                              >
                                Issue materials
                              </Button>
                              <Button
                                size="sm"
                                data-testid={`complete-order-button-${o.id}`}
                                onClick={() => { setFinishOrder(o); setFinish({ produced_quantity: o.quantity, warehouse_id: o.warehouse_id || "", notes: "" }); }}
                              >
                                Finish
                              </Button>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
          </ListState>
        </TabsContent>

        <TabsContent value="boms">
          {boms.length === 0 ? (
            <Empty title="No bills of material" hint="A BOM lists the raw materials needed for one product." />
          ) : (
            <div className="surface overflow-x-auto" data-testid="boms-table">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product</TableHead>
                    <TableHead className="text-right">Output</TableHead>
                    <TableHead className="hidden md:table-cell">Materials</TableHead>
                    <TableHead className="text-right">Material cost</TableHead>
                    <TableHead className="text-right">Cost / unit</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {boms.map((b) => (
                    <TableRow key={b.id} data-testid={`bom-row-${b.id}`}>
                      <TableCell className="text-sm">{b.product_name}</TableCell>
                      <TableCell className="text-right font-mono-num text-sm">{b.output_quantity}</TableCell>
                      <TableCell className="hidden max-w-md text-xs text-muted-foreground md:table-cell">
                        {(b.items || []).map((i) => `${i.name} ${i.quantity}${i.unit}`).join(", ")}
                      </TableCell>
                      <TableCell className="text-right font-mono-num text-sm">{inr(b.material_cost)}</TableCell>
                      <TableCell className="text-right font-mono-num text-sm">{inr(b.cost_per_unit)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={!!issueOrder} onOpenChange={(o) => !o && setIssueOrder(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="issue-materials-dialog">
          <DialogHeader>
            <DialogTitle>Issue materials — {issueOrder?.number}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {(issueOrder?.requirements || []).map((r, i) => (
              <div key={`${r.stock_item_id}-${i}`} className="grid grid-cols-3 items-end gap-2 border-b border-border pb-2">
                <div className="text-sm">
                  {r.name}
                  <div className="text-xs text-muted-foreground">
                    required {r.required_quantity} {r.unit} • issued {r.issued_quantity || 0} • available {r.available_quantity}
                  </div>
                </div>
                <Input
                  data-testid={`issue-quantity-input-${i}`}
                  placeholder="Issue qty"
                  value={issueQty[i] ?? ""}
                  onChange={(e) => setIssueQty({ ...issueQty, [i]: e.target.value })}
                />
                <div className="text-right text-xs text-muted-foreground">
                  {Number(r.shortfall) > 0 ? `Short by ${r.shortfall} ${r.unit}` : "Stock available"}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button data-testid="issue-submit-button" onClick={submitIssue}>Post issue</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!finishOrder} onOpenChange={(o) => !o && setFinishOrder(null)}>
        <DialogContent data-testid="complete-order-dialog">
          <DialogHeader>
            <DialogTitle>Record finished goods — {finishOrder?.number}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3">
            <Field label="Produced quantity">
              <Input
                data-testid="produced-quantity-input"
                value={finish.produced_quantity}
                onChange={(e) => setFinish({ ...finish, produced_quantity: e.target.value })}
              />
            </Field>
            <Field label="Store finished goods in">
              <Select value={finish.warehouse_id} onValueChange={(v) => setFinish({ ...finish, warehouse_id: v })}>
                <SelectTrigger data-testid="finish-warehouse-select">
                  <SelectValue placeholder="Select warehouse" />
                </SelectTrigger>
                <SelectContent>
                  {warehouses.map((w) => (
                    <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Notes">
              <Textarea value={finish.notes} onChange={(e) => setFinish({ ...finish, notes: e.target.value })} />
            </Field>
          </div>
          <DialogFooter>
            <Button data-testid="finish-submit-button" onClick={submitFinish}>Add to stock</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
